import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { chemistryApi } from '../../api/chemistry';
import { invalidatePatentsCache } from '../patents/ProjectPatentsTab';

interface RunIntelligenceButtonProps {
  fileId: string;
  onComplete?: () => void;
  /** Increment to programmatically start enrichment from external code */
  startTrigger?: number;
}

type EnrichMode = 'fast' | 'patents';
type RunStatus = 'idle' | 'running' | 'completed' | 'failed';

/** Format a timestamp string into a human-readable relative time */
const formatTimeAgo = (isoString: string | null): string | null => {
  if (!isoString) return null;
  try {
    const date = new Date(isoString.replace(' ', 'T') + 'Z');
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  } catch {
    return null;
  }
};

const RunIntelligenceButton: React.FC<RunIntelligenceButtonProps> = ({ fileId, onComplete, startTrigger = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMode, setActiveMode] = useState<EnrichMode | null>(null);
  const [status, setStatus] = useState<RunStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusLabel, setStatusLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fastDone, setFastDone] = useState(false);
  const [patentsDone, setPatentsDone] = useState(false);
  const [fastLastRun, setFastLastRun] = useState<string | null>(null);
  const [patentLastRun, setPatentLastRun] = useState<string | null>(null);
  const taskIdRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const cleanup = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  // Load enrichment status on mount to show "already enriched" state
  useEffect(() => {
    let cancelled = false;
    chemistryApi.getEnrichmentInfo(fileId).then((info) => {
      if (cancelled) return;
      if (info.fast_enriched > 0) {
        setFastDone(true);
        setFastLastRun(info.fast_last_run);
      }
      if (info.patent_enriched > 0) {
        setPatentsDone(true);
        setPatentLastRun(info.patent_last_run);
      }
    }).catch(() => {
      // Non-critical — just don't show enrichment state
    });
    return () => { cancelled = true; };
  }, [fileId]);

  // Start fast enrichment when triggered externally
  useEffect(() => {
    if (startTrigger > 0 && status === 'idle') {
      handleStart('fast');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTrigger]);

  // Position menu
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 280;
      let left = rect.left;
      if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;
      setMenuPos({ top: rect.bottom + 4, left });
    }
  }, [isOpen]);

  // Click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleStart = async (mode: EnrichMode) => {
    if (status === 'running') return;
    setIsOpen(false);
    setActiveMode(mode);
    setStatus('running');
    setProgress(0);
    setError(null);
    setStatusLabel(mode === 'fast' ? 'Enriching...' : 'Patent search...');

    try {
      // Patent mode uses patent_collision.py endpoints
      if (mode === 'patents') {
        const numId = parseInt(fileId, 10);
        if (isNaN(numId)) throw new Error('Invalid file ID');
        const scanResult = await chemistryApi.startPatentScan(numId);
        setTotal(scanResult.total_molecules);

        const POLL_TIMEOUT = 300_000;
        const pollStart = Date.now();

        pollIntervalRef.current = setInterval(async () => {
          if (Date.now() - pollStart > POLL_TIMEOUT) {
            cleanup(); setStatus('failed'); setError('Patent scan timed out'); return;
          }
          try {
            const ss = await chemistryApi.getPatentScanStatus(numId);
            setProgress(ss.scanned || ss.progress || 0);
            setTotal(ss.total || scanResult.total_molecules);
            setStatusLabel(`Patent search... ${ss.scanned || ss.progress || 0}/${ss.total || scanResult.total_molecules}`);

            if (ss.status === 'complete' || ss.status === 'completed') {
              cleanup();
              setPatentsDone(true);
              setPatentLastRun(new Date().toISOString());
              setStatusLabel(ss.message || 'Patent search complete');
              invalidatePatentsCache();
              setStatus('completed');
              onComplete?.();
              setTimeout(() => { setStatus('idle'); setActiveMode(null); }, 5000);
            } else if (ss.status === 'failed') {
              setStatus('failed'); setError(ss.error || 'Patent scan failed'); cleanup();
            }
          } catch (err) {
            console.error('Patent poll error:', err);
          }
        }, 2000);
        return;
      }

      // Fast mode uses enrichment.py
      const result = await chemistryApi.startIntelligence(fileId, 'fast');

      // Already enriched — no work to do
      if (result.status === 'completed' && result.task_id === 'none') {
        setStatus('completed');
        setStatusLabel('Already enriched');
        setFastDone(true);
        setTimeout(() => { setStatus('idle'); setActiveMode(null); }, 3000);
        return;
      }

      taskIdRef.current = result.task_id;
      setTotal(result.total);

      const POLL_TIMEOUT = 120_000;
      const pollStart = Date.now();
      let consecutiveErrors = 0;

      pollIntervalRef.current = setInterval(async () => {
        if (!taskIdRef.current) return;

        if (Date.now() - pollStart > POLL_TIMEOUT) {
          cleanup(); setStatus('failed'); setError('Enrichment timed out — check server logs'); return;
        }

        try {
          const sr = await chemistryApi.getIntelligenceStatus(fileId, taskIdRef.current);
          consecutiveErrors = 0;
          setProgress(sr.progress);
          setTotal(sr.total);
          setStatusLabel(`Enriching... ${sr.progress}/${sr.total}`);

          if (sr.status === 'completed') {
            cleanup();
            const nowIso = new Date().toISOString();
            setFastDone(true);
            setFastLastRun(nowIso);
            const s = sr.summary;
            setStatusLabel(
              s ? `Done — ${s.targets || 0} targets, ${s.pathways || 0} pathways, ${s.reactions || 0} reactions`
                : 'Enrichment complete'
            );
            setStatus('completed');
            onComplete?.();
            setTimeout(() => { setStatus('idle'); setActiveMode(null); }, 5000);
          } else if (sr.status === 'failed') {
            setStatus('failed');
            setError(sr.error || 'Enrichment failed');
            cleanup();
          }
        } catch {
          consecutiveErrors++;
          if (consecutiveErrors >= 5) {
            cleanup();
            setStatus('failed');
            setError('Lost connection to server');
          }
        }
      }, 2000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        setStatus('running');
        setStatusLabel('Enrichment already in progress');
        setTimeout(() => { setStatus('idle'); setActiveMode(null); }, 3000);
      } else {
        setStatus('failed');
        setError(detail || err.message || 'Failed to start');
      }
    }
  };

  const handleCancel = () => {
    cleanup();
    taskIdRef.current = null;
    setStatus('idle');
    setActiveMode(null);
    setProgress(0);
    setTotal(0);
  };

  const LightningIcon = ({ accent = false }: { accent?: boolean }) => (
    <svg className={`w-3.5 h-3.5 flex-shrink-0 ${accent ? 'text-amber-300' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  const CheckIcon = () => (
    <svg className="w-3.5 h-3.5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );

  const anyDone = fastDone || patentsDone;

  // Running state
  if (status === 'running') {
    const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-teal-300 bg-teal-900/40 border border-teal-700 rounded-md"
        title={statusLabel}
      >
        <div className="w-4 h-4 border-2 border-teal-600 border-t-teal-300 rounded-full animate-spin flex-shrink-0" />
        <span className="whitespace-nowrap text-xs">
          {progress}/{total}
        </span>
        <div className="w-12 h-1.5 bg-teal-900 rounded-full overflow-hidden flex-shrink-0">
          <div
            className="h-full bg-teal-400 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <button
          onClick={handleCancel}
          className="ml-0.5 w-4 h-4 flex items-center justify-center text-teal-400 hover:text-red-400 hover:bg-red-900/30 rounded transition-colors flex-shrink-0"
          title="Cancel"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  }

  // Completed state (auto-resets after timeout)
  if (status === 'completed') {
    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-green-300 bg-green-900/30 border border-green-700 rounded-md"
        title={statusLabel}
      >
        <CheckIcon />
        <span className="hidden xl:inline text-xs truncate max-w-[180px]">{statusLabel}</span>
      </div>
    );
  }

  // Failed state
  if (status === 'failed') {
    return (
      <button
        onClick={() => activeMode && handleStart(activeMode)}
        className="px-2 py-1.5 text-xs text-red-300 bg-red-900/30 border border-red-700 rounded-md hover:bg-red-900/50 transition-colors flex items-center gap-1.5"
        title={error || 'Retry enrichment'}
      >
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span className="hidden xl:inline text-xs">Retry</span>
      </button>
    );
  }

  // Idle — dropdown button (shows checkmark when enriched)
  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={
          anyDone
            ? "px-2.5 py-1 text-xs font-semibold text-emerald-200 bg-emerald-900/50 border border-emerald-600/50 rounded-md hover:bg-emerald-800/60 transition-all flex items-center gap-1.5"
            : "px-2.5 py-1 text-xs font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 border border-emerald-400/50 rounded-md hover:from-emerald-400 hover:to-teal-400 transition-all flex items-center gap-1.5 shadow-[0_0_8px_rgba(16,185,129,0.3)]"
        }
        title={anyDone ? "Enriched — click to re-run or view details" : "Enrich — Look up targets, pathways, reactions, and patent data"}
      >
        {anyDone ? <CheckIcon /> : <LightningIcon accent />}
        <span className="hidden sm:inline text-xs whitespace-nowrap">{anyDone ? 'Enriched' : 'Enrich'}</span>
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="w-72 bg-gray-900 rounded-xl shadow-xl border border-gray-700 py-1 animate-in fade-in slide-in-from-top-1"
        >
          {/* Fast enrichment option */}
          <button
            onClick={() => handleStart('fast')}
            className="w-full px-3 py-2.5 text-left hover:bg-gray-800 transition-colors group"
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-teal-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-sm font-medium text-gray-200">Targets, Pathways & Reactions</span>
              {fastDone && <CheckIcon />}
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              {fastDone && fastLastRun
                ? `Last run ${formatTimeAgo(fastLastRun)} — click to re-run`
                : 'ChEMBL targets, Reactome pathways, ORD reactions, ADMET predictions'
              }
            </p>
          </button>

          <div className="h-px bg-gray-700 mx-2" />

          {/* Patent search option */}
          <button
            onClick={() => handleStart('patents')}
            className="w-full px-3 py-2.5 text-left hover:bg-gray-800 transition-colors group"
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium text-gray-200">Patent Search</span>
              {patentsDone && <CheckIcon />}
            </div>
            <p className="text-xs text-amber-500/70 mt-1 ml-6">
              {patentsDone && patentLastRun
                ? `Last run ${formatTimeAgo(patentLastRun)} — click to re-run`
                : 'Searches 28.4M patent compounds. May take several minutes.'
              }
            </p>
          </button>
        </div>,
        document.body,
      )}
    </>
  );
};

export default RunIntelligenceButton;
